import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Mic, MicOff, PhoneCall, Send, Trash2, Volume2, VolumeX, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost, apiDelete } from "@/lib/apiAdmin";
import { JarvisOrb } from "./JarvisOrb";
import { useVoice } from "./useVoice";
import { ACTION_LABELS, HUD } from "./theme";

const PROMPTS = [
  "Brief me on today",
  "What's still unpaid?",
  "Confirm all pending bookings",
  "Text my next customer that we're on the way",
  "Which quotes should I chase?",
];

const ActionCard = ({ m, onRun, onDismiss, busy }) => {
  const params = Object.entries(m.action.params || {});
  return (
    <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-3 space-y-2" data-testid="jarvis-action-card">
      <p className={`${HUD.mono} text-[10px] uppercase tracking-[0.25em] text-amber-300`}>
        Awaiting authorization · {ACTION_LABELS[m.action.action] || m.action.action}
      </p>
      {params.length > 0 && (
        <div className="space-y-1 text-xs">
          {params.map(([k, v]) => (
            <p key={k} className="text-gray-300 break-words">
              <span className={`${HUD.mono} text-amber-200/70`}>{k}</span> · {typeof v === "string" ? v : JSON.stringify(v)}
            </p>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={onRun}
          disabled={busy}
          data-testid="jarvis-action-confirm"
          className="flex-1 h-9 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-3.5 h-3.5" /> Authorize</>}
        </button>
        <button
          onClick={onDismiss}
          data-testid="jarvis-action-dismiss"
          className="h-9 px-4 rounded-full border border-white/10 text-gray-400 hover:text-white text-sm"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

const Bubble = ({ m, name, onRun, onDismiss, busy }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className="max-w-[90%] space-y-2">
      {m.role !== "user" && <p className={`${HUD.label} px-1`}>{name}</p>}
      <div
        className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          m.role === "user"
            ? "bg-cyan-400 text-black rounded-tr-sm"
            : "bg-white/[0.03] text-gray-200 border border-cyan-400/10 rounded-tl-sm"
        }`}
      >
        {(m.content || "").replace(/\*\*/g, "")}
      </div>
      {m.action && !m.executed && !m.dismissed && <ActionCard m={m} onRun={onRun} onDismiss={onDismiss} busy={busy} />}
      {m.executed_summary && (
        <div className={`rounded-lg border p-2 flex items-center gap-2 text-xs ${m.executed ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-300" : "border-red-400/30 bg-red-400/5 text-red-300"}`}>
          {m.executed ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {m.executed_summary}
        </div>
      )}
    </div>
  </div>
);

export const JarvisChat = ({ name = "JARVIS-OPS-9", title = "sir", speakDefault = true, className = "", onActivity, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyIdx, setBusyIdx] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("jarvis_voice") !== "off" && speakDefault);
  const [calling, setCalling] = useState(false);
  const scrollRef = useRef(null);
  const voice = useVoice();

  useEffect(() => {
    apiGet("/admin/jarvis/history").then((d) => setMessages(d.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("jarvis_voice", next ? "on" : "off");
    if (!next) voice.hush();
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await apiPost("/admin/assistant/chat", { message: msg });
      const reply = {
        role: "assistant", content: res.answer, action: res.action, message_id: res.message_id,
        executed: !!res.result?.ok, executed_summary: res.result?.summary,
      };
      setMessages((m) => [...m, reply]);
      if (voiceOn) voice.speak(res.answer);
      if (res.auto_executed) onActivity?.();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: e?.response?.data?.detail || `My apologies, ${title} — systems are briefly unavailable.` }]);
    } finally {
      setLoading(false);
    }
  };

  const run = async (idx) => {
    const m = messages[idx];
    setBusyIdx(idx);
    try {
      const res = await apiPost("/admin/assistant/execute", { ...m.action, message_id: m.message_id });
      setMessages((all) => all.map((x, i) => (i === idx ? { ...x, executed: true, executed_summary: res.summary } : x)));
      toast.success(res.summary || "Done");
      onActivity?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyIdx(null);
    }
  };

  const dismiss = (idx) => setMessages((all) => all.map((x, i) => (i === idx ? { ...x, dismissed: true } : x)));

  const clear = async () => {
    await apiDelete("/admin/jarvis/history");
    setMessages([]);
  };

  const callMe = async () => {
    setCalling(true);
    try {
      const r = await apiPost("/admin/jarvis/call", {});
      toast.success(r.status === "stub" ? "Call logged (STUB) — connect Vapi in JARVIS settings to ring your phone" : `Calling ${r.name || r.number}…`);
      onActivity?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Call failed");
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className={`${HUD.panel} flex flex-col overflow-hidden ${className}`} data-testid="jarvis-chat">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-cyan-400/10">
        <JarvisOrb size={34} active={loading || voice.speaking} />
        <div className="min-w-0 flex-1">
          <p className="text-white font-['Outfit'] font-semibold leading-tight truncate">{name}</p>
          <p className={`${HUD.mono} text-[10px] tracking-[0.2em] text-emerald-300 uppercase`}>● Online · you authorize</p>
        </div>
        <button onClick={callMe} disabled={calling} title="Call me" data-testid="jarvis-call-me" className="p-2 rounded-full text-cyan-200 hover:bg-cyan-400/10">
          {calling ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
        </button>
        <button onClick={toggleVoice} title="Voice replies" data-testid="jarvis-voice-toggle" className="p-2 rounded-full text-cyan-200 hover:bg-cyan-400/10">
          {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <button onClick={clear} title="Clear conversation" data-testid="jarvis-clear" className="p-2 rounded-full text-gray-500 hover:text-red-300 hover:bg-red-400/10">
          <Trash2 className="w-4 h-4" />
        </button>
        {onClose && (
          <button onClick={onClose} title="Close" data-testid="jarvis-dock-close" className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="jarvis-messages">
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="rounded-xl border border-cyan-400/10 bg-cyan-400/[0.03] p-4 text-sm text-gray-300">
              At your service, {title}. I can brief you, confirm or reschedule bookings, send counter-offers, add appointments,
              text customers and place calls. Nothing runs until you authorize it — unless you tell me to just do it.
            </div>
            <div className="flex flex-wrap gap-2">
              {PROMPTS.map((q, i) => (
                <button key={q} onClick={() => send(q)} data-testid={`jarvis-prompt-${i}`} className="px-3 py-1.5 rounded-full border border-cyan-400/15 text-xs text-cyan-100/80 hover:bg-cyan-400/10 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble key={m.message_id || `m-${i}`} m={m} name={name} busy={busyIdx === i} onRun={() => run(i)} onDismiss={() => dismiss(i)} />
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-cyan-300/70 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> <span className={HUD.mono}>PROCESSING…</span>
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t border-cyan-400/10 bg-black/30">
        <button
          type="button"
          onClick={() => (voice.listening ? voice.stopListening() : voice.listen((t) => send(t)))}
          data-testid="jarvis-mic"
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${voice.listening ? "bg-red-500 text-white animate-pulse" : "border border-cyan-400/25 text-cyan-200 hover:bg-cyan-400/10"}`}
        >
          {voice.listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <input
          data-testid="jarvis-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={voice.listening ? "Listening…" : `Message ${name}…`}
          disabled={loading}
          className="flex-1 h-10 rounded-full px-4 bg-[#070A0F] border border-cyan-400/15 text-white text-sm placeholder:text-gray-600 outline-none focus:border-cyan-400/50"
        />
        <button type="submit" disabled={loading || !input.trim()} data-testid="jarvis-send" className="w-10 h-10 rounded-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 flex items-center justify-center shrink-0">
          <Send className="w-4 h-4 text-black" />
        </button>
      </form>
    </div>
  );
};

export default JarvisChat;
